import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MatIconRegistry} from '@angular/material/icon';
import {MatPaginator} from '@angular/material/paginator';
import {DomSanitizer} from '@angular/platform-browser';
import {TabManagementService} from '@core-services/tab-management.service';
import {PageEventInterface} from '@core/interfaces/page-event.interface';
import {PageOffering} from '@core/model/side-nav.model';
import {PipelineViewComponent} from '@features/leads/pipeline/pipeline-view/pipeline-view.component';
import {PipelineService} from '@features/leads/pipeline/pipeline.service';
import {GroupedRetailerPipeline, Retailer} from '@features/leads/retailers/retailers.model';
import {ResponsiveDrawerComponent} from '@shared-components/responsive-drawer/responsive-drawer.component';
import {DateUtilsService} from '@shared-services/dateUtils.service';
import {PaginatorService} from '@shared-services/paginator.service';
import {UtilsService} from '@shared-services/utils.service';
import {lastValueFrom, tap} from 'rxjs';
import {SelectableCategory, SelectableIndustry} from "../../../../settings/industries/industry.model";
import {ManageIndustriesService} from "../../../../settings/industries/manage-industries.service";
import {LeaseGridSpaceGroup, OccupiedSpace} from '../../../leasing.model';

@Component({
  selector: 'app-retailers-pipeline',
  templateUrl: './retailers-pipeline.component.html',
  styleUrls: ['./retailers-pipeline.component.scss']
})
export class RetailersPipelineComponent implements OnInit, OnChanges, AfterViewInit {

  @ViewChild('paginator') paginator!: MatPaginator;

  filtersFormGroup: FormGroup;
  allIndustries: SelectableCategory[] = [];

  selectedIndustries: string[] = [];

  displayedColumns: string[] = ['select', 'companyName', 'industryType', 'overallScore'];

  dataSource: any[] = [];
  totalPages: number = 0;
  totalElements: number = 0;

  @Input() selectedSpace!: OccupiedSpace;
  @Input() offering?: PageOffering;
  @Input() selectedLeaseSpaceGroup!: LeaseGridSpaceGroup | null;
  @Output() selectedLeaseSpaceGroupChange: EventEmitter<LeaseGridSpaceGroup> = new EventEmitter<LeaseGridSpaceGroup>();
  private preferredRetailers: Retailer[] = [];
  private preferredIndustries: string[] = [];

  @Output() addRetailer = new EventEmitter<Retailer>();
  @Output() removeRetailer = new EventEmitter<Retailer>();


  constructor(@Inject(ResponsiveDrawerComponent) public responsiveDrawer: ResponsiveDrawerComponent,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer,
              private paginatorService: PaginatorService,
              private pipelineService: PipelineService,
              private tabService: TabManagementService,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private industriesService: ManageIndustriesService,
  ) {
    this.matIconRegistry.addSvgIcon('back-arrow-button', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/back-arrow-button.svg'));
    this.filtersFormGroup = new FormGroup({
      companyName: new FormControl(''),
      industryTypes: new FormControl([])
    });
  }


  ngOnInit(): void {
    lastValueFrom(this.pipelineService.loadQualifiedGroupedApplicants({
      size: '50',
      page: '0',
      sort: 'overallScore,desc'
    }, [{name: 'preferredIndustries', val: this.preferredIndustries.join(',')}
    ]))
      .then((res: GroupedRetailerPipeline) => {
        this.setDatasourceValues(res);
      });
    this.loadIndustries();

    this.filtersFormGroup.get('industryTypes')?.valueChanges.subscribe(value => {
      if (Array.isArray(value)) {
        this.selectedIndustries = value;
        this.loadPipeline();
      }
    });
  }


  ngAfterViewInit(): void {
    this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    this.paginator.page
      .pipe(
        tap(() => this.loadPipeline()))
      .subscribe();
  }

  loadPipeline() {

    const filters = [
      {name: 'industries', val: this.selectedIndustries.join(',')},
      {name: 'companyName', val: this.filtersFormGroup.get('companyName')?.value},
      {name: 'preferredIndustries', val: this.preferredIndustries.join(',')}
    ];
    const page = {
      size: this.paginator.pageSize.toString(),
      page: this.paginator.pageIndex.toString(),
      sort: 'overallScore,desc'
    };
    lastValueFrom(this.pipelineService.loadQualifiedGroupedApplicants(page, filters))
      .then((res: GroupedRetailerPipeline) => {
        this.setDatasourceValues(res);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    let loadPipeline: boolean = false;
    if (changes['selectedSpace']) {
      this.selectedSpace = changes['selectedSpace'].currentValue;
      if (this.selectedSpace && this.selectedSpace.districtSpace.districtSpacesIndustries && this.selectedSpace.districtSpace.districtSpacesIndustries.length > 0) {
        loadPipeline = true;
      }
    }
    if (changes['selectedLeaseSpaceGroup']) {
      this.selectedLeaseSpaceGroup = changes['selectedLeaseSpaceGroup'].currentValue;
    }
    this.resetIndustrySelection(loadPipeline);
    this.setRetailers();
  }

  //update loader function
  loadIndustries() {
    lastValueFrom(this.industriesService.getAllSelectableOptionsForOffering())
      .then((res: SelectableCategory[]) => {
        this.allIndustries = res.map(category => ({
          ...category,
          industries: (category.industries ?? []).map(ind => ({
            ...ind,
            selected: true,
          })),
          selected: category.selected
        })) as SelectableCategory[];
      });
  }

  removeFromSelectedIndustries(industryCode: string): void {
    const currentSelection = this.filtersFormGroup.get('industryTypes')?.value || [];
    const newSelection = currentSelection.filter((code: string) => code !== industryCode);
    this.filtersFormGroup.get('industryTypes')?.setValue(newSelection);
  }

  isGroup(index: any, item: any): boolean {
    return item.isGroup;
  }

  getSpaceName(): string {
    if (this.selectedSpace) {
      return this.selectedSpace.districtSpace.spaceName;
    } else {
      return '';
    }
  }

  openPipelineViewTab(id: string) {
    let payload: PageEventInterface = {
      componentToRegister: PipelineViewComponent,
      pageName: 'Pipeline ' + id,
      pageHeader: this.offering!.label,
      data: {id: id},
      id: this.utils.generateUuidWithPrefix('leads'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'leads',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  getRetailerIndustries(listString: string, max: number) {
    if (listString) return listString.split(',')
      .map(m => this.allIndustries
        .flatMap(fm => fm.industries)
        .find(f => f.code == m))
      .filter(f => !!f)
      .map(m => m!.labelNameOverride ?? m!.name)
      .slice(0, max);
    return null;
  }

  addRetailerToShortlist(element: Retailer): void {
    this.addRetailer.emit(element);
  }

  removeRetailerFromShortlist(element: Retailer): void {
    this.removeRetailer.emit(element);
  }

  setRetailers() {
    if (this.selectedLeaseSpaceGroup && this.selectedLeaseSpaceGroup.leaseGridSpaces) {
      const sorted = this.selectedLeaseSpaceGroup?.leaseGridSpaces
        .filter(f => f.retailerId)
        .sort((a, b) => b.periodGroupRanking! - a.periodGroupRanking!)!;
      this.preferredRetailers = sorted
        .map(m => new Retailer({id: m.retailerId, companyName: m.retailerCompanyName}));
    }
  }

  private setDatasourceValues(res: GroupedRetailerPipeline): void {
    this.dataSource = [];
    this.dataSource.push({group: 'Best Suited', isGroup: true, styleClass: 'table-group'});
    if (res.bestSuited.length == 0) {
      this.dataSource.push({
        group: 'There are no recommended retailers for the current filters',
        subText: 'Adjust the filters to update recommendations',
        isGroup: true,
        styleClass: 'table-message-bold'
      });
    } else {
      this.dataSource.push(...res.bestSuited);
    }
    this.dataSource.push({group: 'Other', isGroup: true, styleClass: 'table-group'});
    this.totalElements = res.retailerPipelines.totalElements;
    this.totalPages = res.retailerPipelines.totalPages;
    this.dataSource.push(...res.retailerPipelines.content);

  }

  private resetIndustrySelection(loadPipeline: boolean = false): void {
    this.preferredIndustries = [];
    if (this.selectedSpace && this.selectedSpace.districtSpace.districtSpacesIndustries) {
      let industryCategoryCodes: string[] = this.selectedSpace.districtSpace.districtSpacesIndustries.map(ind => ind);
      this.allIndustries.forEach((industryCategory: SelectableCategory) => {
        if (industryCategoryCodes.includes(industryCategory.code)) {
          industryCategory.industries.forEach((ind: SelectableIndustry) => {
            ind.selected = true;
          });
          industryCategory.selected = true;
          this.preferredIndustries.push(...industryCategory.industries.map((ind: { code: any; }) => ind.code));
        }
      });

      // Set the form control value to the preferred industries
      this.filtersFormGroup.get('industryTypes')?.setValue(this.preferredIndustries);
      this.selectedIndustries = this.preferredIndustries;
    }
    if (loadPipeline) {
      this.loadPipeline();
    }
  }

  isPreferredRetailer(element: Retailer): Boolean {
    return this.preferredRetailers.map(ret => ret.id).includes(element.id)
  }

}
